import { motion } from 'framer-motion'
import { WhatsappLink } from '..'
import { FaArrowRight, FaPlay, FaRocket, FaRobot, FaMobile, FaChartLine } from 'react-icons/fa'

const Hero = () => {

  return (
    <section className="md:pt-5 pt-12 relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-900 via-orange-300 to-orange-100">
      {/* Simplified Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Static gradient orbs - no animation for better performance */}
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-radial from-white-500/20 to-transparent rounded-full blur-xl opacity-30" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-radial from-pink-500/15 to-transparent rounded-full blur-2xl opacity-20" />
      </div>

      <div className="max-w-7xl mx-auto px-6 grid gap-16 lg:grid-cols-2 items-center relative z-10 pt-10 mb-10">
        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center lg:text-left"
        >
          {/* Language Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center justify-center lg:justify-start gap-4 mb-6"
          >

          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl lg:text-6xl font-bold leading-relaxed mb-8 "
          >

            <span className="bg-gradient-to-r from-orange-300 via-orange-300 to-white bg-clip-text text-transparent">
              Dine.ai
            </span>
            <br />
            <span className="text-purple-100">Where Intelligence</span>
            <br />
            <span className="text-white">Meets Experience</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-6"
          >
            <p className="text-xl lg:text-2xl mb-4 leading-relaxed text-white">
              Transform your restaurant into a smart, connected ecosystem. <br /> 
              From AI-powered menus to conversational ordering every interaction feels effortless.
            </p>
          </motion.div>



          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col md:flex-row md:justify-center lg:justify-start gap-4 mb-6"
          >
            <motion.button
              whileHover={{
              scale: 1.05,
              boxShadow: "0 25px 50px rgba(249, 115, 22, 0.4)",
              }}
              whileTap={{ scale: 0.95 }}
              className="bg-white/90 backdrop-blur-md text-orange-500 px-8 py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 hover:shadow-2xl transition-all duration-300 group border border-white/50"
              onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <FaPlay className="group-hover:scale-110 transition-transform duration-300" />
              See It in Action
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </motion.button>

            <motion.a
              href={WhatsappLink}
              target="_blank"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="border-2 border-white/50 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 backdrop-blur-md transition-all duration-300 flex items-center justify-center gap-3 group"
            >
              <FaRocket className="group-hover:rotate-12 transition-transform duration-300" />
              Get Started
            </motion.a>
          </motion.div>


        </motion.div>

        {/* Smart Ecosystem Visual */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative hidden md:block"
        >
          {/* Floating 3D Screens */}
          <div className="relative mx-auto w-full max-w-lg h-96 perspective-1000">
            {/* Main Menu Screen */}
            <motion.div
              animate={{
                rotateY: [-5, 5, -5],
                y: [-10, 10, -10],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute top-0 left-0 w-64 h-80 bg-white/90 backdrop-blur-xl rounded-2xl p-4 shadow-2xl border border-white/50 transform-gpu"
              style={{ transform: 'rotateX(10deg) rotateY(-10deg)' }}
            >
              <div className="h-full bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 relative">
                <div className="flex items-center gap-2 mb-4">
                  <FaMobile className="text-purple-600" />
                  <span className="font-bold text-purple-900 text-sm">Menu (English/العربية)</span>
                </div>
                {/* Menu Items - Dine.ai details */}
                <div className="space-y-3">
                  <div className="flex gap-3 bg-white/80 rounded-lg p-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center text-xl">🥗</div>
                    <div className="flex-1">
                      <div className="font-bold text-purple-900">Greek Salad <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Vegetarian</span> <span className="ml-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Halal</span></div>
                      <div className="text-xs text-gray-700">280 cal • 12 min • طماطم، خيار، زيتون، جبنة فيتا</div>
                    </div>
                  </div>
                  <div className="flex gap-3 bg-white/80 rounded-lg p-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center text-xl">🍗</div>
                    <div className="flex-1">
                      <div className="font-bold text-purple-900">Spicy Wings <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Keto</span></div>
                      <div className="text-xs text-gray-700">420 cal • 18 min • دجاج، بهارات، صوص حار</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Chat Interface Screen */}
            <motion.div
              animate={{
                rotateY: [5, -5, 5],
                y: [10, -10, 10],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute top-16 right-0 w-56 h-72 bg-white/90 backdrop-blur-xl rounded-2xl p-4 shadow-2xl border border-white/50 transform-gpu"
              style={{ transform: 'rotateX(-10deg) rotateY(15deg)' }}
            >
              <div className="h-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-3 relative">
                <div className="flex items-center gap-2 mb-3">
                  <FaRobot className="text-blue-600" />
                  <span className="font-bold text-blue-900 text-sm">AI Chat</span>
                  <div className="ml-auto w-2 h-2 bg-green-400 rounded-full"></div>
                </div>
                <div className="space-y-2 text-xs">
                  {/* Customer message - left aligned, light background */}
                  <motion.div
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="bg-white rounded-lg p-2 text-gray-700 text-left w-fit"
                    style={{ maxWidth: '80%' }}
                  >
                    <span className="font-semibold text-gray-600">Customer:</span> "I need vegetarian options under $10"
                  </motion.div>
                  {/* AI message - right aligned, purple background */}
                  <motion.div
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    className="bg-purple-600 text-white rounded-lg p-2 text-right ml-auto w-fit"
                    style={{ maxWidth: '80%' }}
                  >
                    <span className="font-semibold text-white">AI:</span> "Greek Salad: 280 cal, 12 min prep 🥗"
                  </motion.div>
                  {/* AI follow-up - right aligned, purple background */}
                  <motion.div
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                    className="bg-purple-600 text-white rounded-lg p-2 text-right ml-auto w-fit"
                    style={{ maxWidth: '80%' }}
                  >
                    <span className="font-semibold text-white">AI:</span> "Would you like to add it to your order?"
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Analytics Dashboard Screen */}
            <motion.div
              animate={{
                rotateX: [10, -10, 10],
                y: [5, -5, 5],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute bottom-0 left-12 w-60 h-64 bg-white/90 backdrop-blur-xl rounded-2xl p-4 shadow-2xl border border-white/50 transform-gpu"
              style={{ transform: 'rotateX(5deg) rotateY(10deg)' }}
            >
              <div className="h-full bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-3 relative">
                <div className="flex items-center gap-2 mb-3">
                  <FaChartLine className="text-green-600" />
                  <span className="font-bold text-green-900 text-sm">Analytics Dashboard</span>
                </div>
                {/* Chart Bars & Metrics */}
                <div className="mb-2">
                  <div className="font-bold text-orange-700">Cappuccino: 103 clicks</div>
                  <div className="text-xs text-gray-700 mb-1">Avg time: 979s</div>
                </div>
                <div className="flex items-end gap-2 h-20 mb-3">
                  {[40, 60, 80, 55, 90].map((height, i) => (
                    <motion.div
                      key={i}
                      animate={{ height: [`${height}%`, `${height + 10}%`, `${height}%`] }}
                      transition={{ duration: 3, repeat: Infinity, delay: i * 0.2 }}
                      className="flex-1 bg-gradient-to-t from-orange-500 to-orange-100 rounded-sm"
                    />
                  ))}
                </div>
                <div className="text-xs text-gray-600">
                  <div className="flex justify-between mb-1">
                    <span>Sales</span>
                    <span className="text-green-600 font-bold">+23%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>AI Orders</span>
                    <span className="text-blue-600 font-bold">+45%</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Restaurant Scene Background */}
          <div className="absolute -z-10 right-0 top-1/2 transform -translate-y-1/2 w-96 h-96 opacity-30">
            <div className="relative w-full h-full bg-gradient-to-br from-white/10 to-purple-500/10 rounded-3xl backdrop-blur-sm">
              {/* Restaurant elements */}
              <div className="absolute top-8 left-8 w-16 h-16 bg-white/20 rounded-xl backdrop-blur-md"></div>
              <div className="absolute bottom-8 right-8 w-20 h-12 bg-purple-300/20 rounded-lg backdrop-blur-md"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-pink-300/20 rounded-full backdrop-blur-md"></div>

              {/* Glowing accents */}
              <motion.div
                animate={{ opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute top-4 right-4 w-3 h-3 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50"
              ></motion.div>
              <motion.div
                animate={{ opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                className="absolute bottom-4 left-4 w-2 h-2 bg-pink-400 rounded-full shadow-lg shadow-pink-400/50"
              ></motion.div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  )
}

export default Hero